import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { warehouseAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, RefreshCw, Plus, Minus, Pencil, Trash2 } from 'lucide-react';

export const WarehouseStock = () => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [targetEntry, setTargetEntry] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualAsin, setManualAsin] = useState('');
  const [manualEan, setManualEan] = useState('');
  const [manualQuantity, setManualQuantity] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const fetchStock = async () => {
    setLoading(true);
    try {
      const response = await warehouseAPI.getStock();
      setStock(response.data);
    } catch (error) {
      toast.error('Failed to load warehouse stock');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (entry) => {
    setTargetEntry(entry);
    setEditQuantity(entry.quantity.toString());
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!targetEntry) return;
    const parsed = parseInt(editQuantity, 10);
    if (Number.isNaN(parsed)) {
      toast.error('Enter a valid quantity');
      return;
    }
    try {
      await warehouseAPI.setStockQuantity(targetEntry.product_id, parsed);
      toast.success('Stock quantity updated');
      setEditDialogOpen(false);
      setTargetEntry(null);
      fetchStock();
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Remove ${entry.name} from warehouse stock?`)) return;
    try {
      await warehouseAPI.deleteStockEntry(entry.product_id);
      toast.success('Stock entry deleted');
      fetchStock();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleManualSubmit = async () => {
    if (!manualAsin.trim() && !manualEan.trim()) {
      toast.error('Enter an ASIN or EAN');
      return;
    }
    const qty = parseInt(manualQuantity, 10);
    if (Number.isNaN(qty)) {
      toast.error('Enter a valid quantity');
      return;
    }
    setManualSubmitting(true);
    try {
      const payload = {
        asin: manualAsin.trim() || undefined,
        ean: manualEan.trim() || undefined,
        quantity: qty,
      };
      await warehouseAPI.addStockManual(payload);
      toast.success('Stock entry added');
      setManualDialogOpen(false);
      setManualAsin('');
      setManualEan('');
      setManualQuantity('');
      fetchStock();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add stock';
      toast.error(message);
    } finally {
      setManualSubmitting(false);
    }
  };

  useEffect(() => {
    fetchStock();
  }, []);

  const handleAdjustStock = async (productId, adjustment) => {
    try {
      await warehouseAPI.adjustStock(productId, adjustment);
      toast.success(`Stock ${adjustment > 0 ? 'increased' : 'decreased'} by ${Math.abs(adjustment)}`);
      fetchStock();
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  const filteredStock = stock.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.ean.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.asin.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalUnits = stock.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Layout>
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title" data-testid="warehouse-stock-title">Warehouse Stock</h1>
          <p className="page-subtitle">Current physical inventory in your warehouse</p>
        </div>
        <Button variant="outline" onClick={fetchStock} disabled={loading} data-testid="refresh-stock-btn">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total SKUs</p>
            <p className="text-3xl font-bold text-slate-900">{stock.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Units</p>
            <p className="text-3xl font-bold text-slate-900">{totalUnits.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Avg Units/SKU</p>
            <p className="text-3xl font-bold text-slate-900">
              {stock.length > 0 ? Math.round(totalUnits / stock.length) : 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="warehouse-stock-card">
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <CardTitle className="text-lg">Stock Levels</CardTitle>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, EAN, or ASIN..."
                className="pl-9 w-72"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="stock-search-input"
              />
            </div>
            <Button onClick={() => setManualDialogOpen(true)} data-testid="open-manual-stock-dialog">
              Add Stock
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Name</TableHead>
                  <TableHead>EAN</TableHead>
                  <TableHead>ASIN</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Quick Adjust</TableHead>
                  <TableHead className="text-right">Manage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStock.map((item) => (
                  <TableRow key={item.product_id} data-testid={`stock-row-${item.product_id}`}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="font-mono text-xs">{item.ean}</TableCell>
                    <TableCell className="font-mono text-xs">{item.asin}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-bold ${item.quantity < 50 ? 'text-red-600' : item.quantity < 100 ? 'text-amber-600' : 'text-slate-900'}`}>
                        {item.quantity}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(item.last_updated).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleAdjustStock(item.product_id, -1)}
                          data-testid={`decrease-stock-${item.product_id}`}
                        >
                          <Minus size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleAdjustStock(item.product_id, 1)}
                          data-testid={`increase-stock-${item.product_id}`}
                        >
                          <Plus size={14} />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(item)}>
                          <Pencil size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteEntry(item)}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredStock.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      {searchTerm ? 'No items match your search' : 'No stock data available. Seed sample data from the dashboard.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setTargetEntry(null);
            setEditQuantity('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Quantity</DialogTitle>
            <DialogDescription>
              {targetEntry ? `${targetEntry.name} (${targetEntry.ean})` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Quantity</Label>
            <Input
              type="number"
              value={editQuantity}
              onChange={(e) => setEditQuantity(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={manualDialogOpen}
        onOpenChange={(open) => {
          setManualDialogOpen(open);
          if (!open) {
            setManualAsin('');
            setManualEan('');
            setManualQuantity('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Stock from ASIN</DialogTitle>
            <DialogDescription>Provide an ASIN and quantity; product info comes from the ASIN Database.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>ASIN</Label>
              <Input
                value={manualAsin}
                onChange={(e) => setManualAsin(e.target.value)}
                placeholder="B09XXXXXXX"
              />
              <p className="text-xs text-slate-500 mt-1">Optional if EAN is provided.</p>
            </div>
            <div>
              <Label>EAN</Label>
              <Input
                value={manualEan}
                onChange={(e) => setManualEan(e.target.value)}
                placeholder="4006381333931"
              />
              <p className="text-xs text-slate-500 mt-1">Optional if ASIN is provided.</p>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={manualQuantity}
                onChange={(e) => setManualQuantity(e.target.value)}
                placeholder="100"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleManualSubmit} disabled={manualSubmitting}>
              {manualSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
